import React, { useEffect, useState } from 'react';
import { MDTypography, MDInput, MDButton, FaIcon } from '@components';
import { FormHelperText, Grid, Switch, Tooltip } from '@mui/material';
import { sendAmplitudeData } from '@utils/amplitude';
import { ADDED_METER, CLICKED_ADD_METER } from '@constants/eventNames';

interface Props {
  setKpiTarget: (target: number | null) => void;
  kpiTarget?: number | null;
}

const Meter: React.FC<Props> = ({ setKpiTarget, kpiTarget }) => {
  const [inputTarget, setInputTarget] = useState('');

  useEffect(() => {
    if (typeof kpiTarget !== 'undefined' && kpiTarget !== null) {
      setInputTarget(String(kpiTarget));
    } else {
      setUserToggle(false);
      setInputTarget('');
    }
  }, [kpiTarget]);

  const [errorMessage, setErrorMessage] = useState('');
  const [userToggle, setUserToggle] = useState(kpiTarget ? true : false);

  const onTargetSubmit = () => {
    if (inputTarget.trim() === '') {
      setErrorMessage('Please enter a number and click OK');

      return;
    }
    if (isNaN(Number(inputTarget.trim()))) {
      setErrorMessage('Please enter a number only and click OK');

      return;
    }

    if (!isNaN(Number(inputTarget.trim()))) {
      setKpiTarget(Number(inputTarget));
      sendAmplitudeData(ADDED_METER);
    }
  };

  return (
    <Grid
      container
      display='flex'
      flexDirection='row'
      flexWrap='nowrap'
      height='45px'
      mb='0.5rem'
    >
      <Grid item>
        <Tooltip
          title={
            <span>
              Please input a target number to display the actual and target
              numbers for the KPI represented on a meter
              <div style={{ display: 'inline-block', marginInline: '5px' }}>
                <FaIcon
                  icon='gauge-max'
                  typographyProps={{
                    color: 'white',
                    variant: 'xs',
                    textGradient: 'true'
                  }}
                />
              </div>
              <div>
                For example, for the Revenue KPI, if the actual value is
                650,500, you may set the target as 800,000.
              </div>
            </span>
          }
          placement='top'
          sx={{ pointerEvents: 'auto' }}
        >
          <Switch
            checked={
              typeof kpiTarget !== 'undefined' && kpiTarget !== null
                ? true
                : false || userToggle
            }
            onChange={() => {
              if (userToggle || kpiTarget) {
                setErrorMessage('');
                setInputTarget('');
                setKpiTarget(null);
              } else {
                sendAmplitudeData(CLICKED_ADD_METER);
              }
              setUserToggle(!userToggle);
            }}
          />
        </Tooltip>
      </Grid>
      <Grid item ml={1}>
        <Grid container direction='column'>
          <Grid item>
            <MDTypography variant='sm' color='info'>
              Show KPI as Meter
            </MDTypography>
          </Grid>
          {(inputTarget || userToggle) && (
            <Grid
              container
              direction='row'
              display='flex'
              alignItems='center'
              flexWrap='nowrap'
            >
              <MDTypography
                variant='h6'
                sx={{ display: 'inline-block', mr: 3 }}
              >
                Input Target
              </MDTypography>
              <Tooltip
                title={
                  <span>
                    Please input a target number to display the actual and
                    target numbers for the KPI represented on a meter
                    <div
                      style={{ display: 'inline-block', marginInline: '5px' }}
                    >
                      <FaIcon
                        icon='gauge-max'
                        typographyProps={{
                          color: 'white',
                          variant: 'xs',
                          textGradient: 'true'
                        }}
                      />
                    </div>
                    <div>
                      For example, for the Revenue KPI, if the actual value is
                      650,500, you may set the target as 800,000.
                    </div>
                  </span>
                }
                placement='top'
              >
                <span>
                  <FaIcon
                    icon='circle-question'
                    style={{
                      verticalAlign: 'middle'
                    }}
                    typographyProps={{
                      color: 'info'
                    }}
                  />
                </span>
              </Tooltip>
              <MDInput
                sx={{ marginX: '10px', width: '90px' }}
                value={inputTarget || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (errorMessage) setErrorMessage('');
                  setInputTarget(e.target.value);
                }}
              />
              <MDButton
                color='primary'
                variant='gradient'
                ml={1}
                onClick={() => onTargetSubmit()}
              >
                OK
              </MDButton>
            </Grid>
          )}
          {errorMessage && (
            <FormHelperText error>{errorMessage}</FormHelperText>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Meter;
